import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getSocket } from '@/lib/ws/socket';
import { getManualHolds } from './api';

export const manualHoldsKey = (caseUrn: string, subCaseUrn: string) =>
  ['cases', caseUrn, 'manual-holds', subCaseUrn] as const;

/** Server recovery plus case-room updates; no polling or invented expiry. */
export function useManualHolds(caseUrn: string, subCaseUrn: string) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: manualHoldsKey(caseUrn, subCaseUrn),
    queryFn: () => getManualHolds(caseUrn, subCaseUrn),
    enabled: !!caseUrn && !!subCaseUrn,
    retry: false,
  });

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const refresh = () => { void qc.invalidateQueries({ queryKey: manualHoldsKey(caseUrn, subCaseUrn) }); };
    const onChange = (payload: unknown) => {
      if (!payload || typeof payload !== 'object') return;
      const event = payload as { caseUrn?: string; subCaseUrn?: string; kind?: string };
      if (event.caseUrn === caseUrn && (!event.subCaseUrn || event.subCaseUrn === subCaseUrn) &&
        (event.kind === 'booking' || event.kind === 'allocation')) refresh();
    };
    socket.on('CaseChanged', onChange);
    socket.on('connect', refresh);
    return () => {
      socket.off('CaseChanged', onChange);
      socket.off('connect', refresh);
    };
  }, [caseUrn, subCaseUrn, qc]);
  return query;
}
